import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PwaService } from '../../../core/services/pwa.service';
import { IconsModule } from '../../icons.module';

/**
 * Non-blocking install card.
 * Intentionally does NOT use a full-screen dimmed overlay — that froze the UI
 * and fought the side menu when returning logged-in users opened the site.
 */
@Component({
  standalone: true,
  imports: [CommonModule, IconsModule],
  selector: 'app-pwa-install-banner',
  template: `
    @if (pwaService.shouldShowBanner()) {
      <div class="install-host" role="region" aria-label="Install app">
        <div
          class="install-sheet"
          role="dialog"
          aria-labelledby="pwa-install-title"
          aria-modal="false"
        >
          <button
            type="button"
            class="close-btn"
            (click)="pwaService.dismissBanner()"
            aria-label="Close"
          >
            <i-lucide name="x" [size]="18"></i-lucide>
          </button>

          <div class="sheet-row">
            <img
              class="app-icon"
              [src]="pwaService.iconSrc()"
              alt=""
              width="48"
              height="48"
            />
            <div class="sheet-copy">
              <h2 id="pwa-install-title" class="app-name">LTNG Market</h2>
              <p class="app-subtitle">Install for faster access</p>
            </div>
          </div>

          @if (pwaService.platform() === 'ios') {
            <div class="install-instructions">
              <div class="instruction-step">
                <span class="step-number">1</span>
                <span>Tap <strong>Share</strong> <span class="ios-icon">⬆</span> in Safari</span>
              </div>
              <div class="instruction-step">
                <span class="step-number">2</span>
                <span>Choose <strong>Add to Home Screen</strong></span>
              </div>
              <div class="instruction-step">
                <span class="step-number">3</span>
                <span>Tap <strong>Add</strong></span>
              </div>
            </div>
            <button type="button" class="secondary-btn" (click)="pwaService.dismissBanner()">
              Not now
            </button>
          } @else {
            <div class="actions">
              <button type="button" class="secondary-btn" (click)="pwaService.dismissBanner()">
                Not now
              </button>
              <button type="button" class="install-btn" (click)="pwaService.installPwa()">
                <i-lucide name="download" [size]="16"></i-lucide>
                Install
              </button>
            </div>
          }
        </div>
      </div>
    }
  `,
  styles: [`
    /* Host does not capture clicks outside the card — menu stays usable */
    .install-host {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 9000;
      display: flex;
      justify-content: center;
      padding: 0 12px calc(12px + env(safe-area-inset-bottom, 0px));
      pointer-events: none;
      animation: fadeIn 0.25s ease;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .install-sheet {
      pointer-events: auto;
      position: relative;
      width: min(100%, 420px);
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 16px;
      padding: 14px 14px 12px;
      box-shadow:
        0 12px 32px rgba(15, 23, 42, 0.14),
        0 4px 12px rgba(15, 23, 42, 0.06);
      animation: slideUp 0.32s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    }

    @keyframes slideUp {
      from {
        transform: translateY(110%);
        opacity: 0.85;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    .close-btn {
      position: absolute;
      top: 8px;
      right: 8px;
      background: #f1f5f9;
      border: 1px solid #e5e7eb;
      color: #6b7280;
      cursor: pointer;
      padding: 5px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .close-btn:hover {
      background: #e8effe;
      color: #1E4ED8;
    }

    .sheet-row {
      display: flex;
      align-items: center;
      gap: 12px;
      padding-right: 28px;
      margin-bottom: 12px;
    }

    .app-icon {
      display: block;
      width: 48px;
      height: 48px;
      border-radius: 12px;
      flex-shrink: 0;
      box-shadow: 0 4px 12px rgba(30, 78, 216, 0.2);
    }

    .sheet-copy {
      min-width: 0;
    }

    .app-name {
      margin: 0 0 2px;
      font-size: 16px;
      font-weight: 700;
      color: #0f172a;
      letter-spacing: -0.01em;
    }

    .app-subtitle {
      margin: 0;
      font-size: 13px;
      color: #64748b;
      font-weight: 500;
    }

    .actions {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .install-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      flex: 1;
      padding: 11px 14px;
      background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
      color: #ffffff;
      border: none;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 600;
      font-family: var(--font-family, inherit);
      cursor: pointer;
    }

    .install-btn:active {
      transform: scale(0.98);
    }

    .secondary-btn {
      flex: 0 0 auto;
      min-width: 96px;
      padding: 11px 12px;
      background: #f8fafc;
      color: #64748b;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 500;
      font-family: var(--font-family, inherit);
      cursor: pointer;
    }

    .secondary-btn:hover {
      background: #f1f5f9;
      color: #1f2937;
    }

    .install-instructions {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-bottom: 10px;
      padding-top: 4px;
    }

    .instruction-step {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 13px;
      color: #6b7280;
      text-align: left;
    }

    .step-number {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: #eff6ff;
      color: #1E4ED8;
      font-size: 12px;
      font-weight: 700;
      flex-shrink: 0;
    }

    .ios-icon {
      display: inline-flex;
      font-size: 14px;
    }

    /* Keep above mobile bottom nav padding without covering the whole screen */
    @media (max-width: 767px) {
      .install-host {
        padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px));
      }
    }
  `],
})
export class PwaInstallBannerComponent {
  readonly pwaService = inject(PwaService);
}
